import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import NotificationBell from '../../components/admin/NotificationBell';
import MessageNotificationBell from '../../components/admin/MessageNotificationBell';
import FormStatusBadge from '../../components/forms/FormStatusBadge';
import { normalizePhoneForLookup, generateGuestPseudoEmail } from '../../utils/phone';

// Add custom CSS for dropdowns
const dropdownStyles = `
  .dropdown {
    position: relative;
    display: inline-block;
  }
  
  .dropdown-menu {
    display: none;
    position: absolute;
    right: 0;
    z-index: 30;
    min-width: 10rem;
    margin-top: 0.5rem;
    background-color: white;
    border-radius: 0.375rem;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.05), 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  }
  
  .dropdown:hover .dropdown-menu {
    display: block;
  }
`;

// Define interfaces based on the database schema
interface User {
  id: string;
  email: string;
  created_at: string;
  last_sign_in_at: string | null;
  user_metadata: {
    first_name?: string;
    last_name?: string;
    full_name?: string;
    name?: string;
  };
}

interface UserProfile {
  id: string;
  full_name: string | null;
  preferred_name: string | null;
  phone_number: string | null;
  account_status: 'active' | 'inactive' | 'suspended';
  profile_picture_url: string | null;
  birth_date: string | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  country: string | null;
  gender: string | null;
  pronouns: string | null;
  language_preference: string | null;
  wallet_balance?: number;
  facial_consent_form_id: string | null;
  created_at: string;
  updated_at: string;
}

interface UserRole {
  id: string;
  user_id: string;
  role: string;
  created_at: string;
}

interface ClientPreferences {
  user_id: string;
  preferred_therapists: string[] | null;
  favorite_services: string[] | null;
  email_notifications: boolean;
  sms_notifications: boolean;
  push_notifications: boolean;
  marketing_emails: boolean;
  special_requests: string | null;
  room_temp_preference: 'cool' | 'moderate' | 'warm' | null;
  music_preference: string | null;
  pressure_preference: 'light' | 'medium' | 'firm' | 'deep' | null;
  created_at: string;
  updated_at: string;
}

interface HealthProfile {
  id: string;
  user_id: string;
  medical_conditions: string[] | null;
  allergies: string[] | null;
  is_pregnant: boolean;
  medications: string[] | null;
  contraindications: string[] | null;
  emergency_contact_name: string | null;
  emergency_contact_phone: string | null;
  emergency_contact_relationship: string | null;
  waiver_signed: boolean;
  waiver_signed_date: string | null;
  created_at: string;
  updated_at: string;
}

interface LoyaltyAccount {
  user_id: string;
  points: number;
  tier: 'standard' | 'silver' | 'gold' | 'platinum';
  membership_expiry: string | null;
  referral_code: string;
  referral_count: number;
  available_rewards: string[] | null;
  created_at: string;
  updated_at: string;
}

// Combined user data for display
interface UserData {
  id: string;
  email: string;
  full_name: string;
  role: string;
  account_status: string;
  created_at: string;
  profile: UserProfile | null;
  preferences: ClientPreferences | null;
  health: HealthProfile | null;
  loyalty: LoyaltyAccount | null;
  membership_status: string | null;
  membership_started_at: string | null;
  stripe_subscription_id: string | null;
  consent_form_token: string | null;
  consent_form_signed: boolean | null;
}

// Props interface for mode support
interface UserManagementProps {
  mode?: 'admin' | 'crew';
}

// User status badge component
interface StatusBadgeProps {
  status: string;
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ status }) => {
  const statusStyles = {
    active: 'bg-green-100 text-green-800',
    inactive: 'bg-gray-100 text-gray-800',
    suspended: 'bg-red-100 text-red-800',
    pending: 'bg-yellow-100 text-yellow-800',
    guest: 'bg-blue-100 text-blue-800',
    pending_verification: 'bg-yellow-100 text-yellow-800',
    deleted: 'bg-gray-400 text-white',
  };

  const style = statusStyles[status as keyof typeof statusStyles] || statusStyles.inactive;

  const displayText = status === 'pending_verification' ? 'Pending' : status.charAt(0).toUpperCase() + status.slice(1);

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${style}`}>
      {displayText}
    </span>
  );
};

// Toast notification component
interface Toast {
  id: number;
  message: string;
  type: 'success' | 'error' | 'info';
}

const Toast = ({ toast, onClose }: { toast: Toast; onClose: () => void }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 3000);
    
    return () => clearTimeout(timer);
  }, [onClose]);
  
  const bgColor = toast.type === 'success' 
    ? 'bg-green-500' 
    : toast.type === 'error' 
      ? 'bg-red-500' 
      : 'bg-blue-500';
  
  return (
    <div className={`${bgColor} text-white px-4 py-3 rounded shadow-lg flex justify-between items-center`}>
      <span>{toast.message}</span>
      <button 
        onClick={onClose}
        className="ml-4 text-white focus:outline-none"
      >
        ×
      </button>
    </div>
  );
};

interface UserDetailsModalProps {
  user: UserData;
  onClose: () => void;
  onSave: (updatedUser: UserData) => void;
}

const UserDetailsModal: React.FC<UserDetailsModalProps> = ({ user, onClose, onSave }) => {
  // Local state for all editable fields
  const [form, setForm] = useState<UserData>({
    ...user,
    profile: user.profile ? { ...user.profile } : null,
    preferences: user.preferences ? { ...user.preferences } : null,
    health: user.health ? { ...user.health } : null,
    loyalty: user.loyalty ? { ...user.loyalty } : null,
  });

  // Helper to update nested fields
  const updateField = (section: keyof UserData, key: string, value: any) => {
    setForm(prev => ({
      ...prev,
      [section]: prev[section] ? { ...prev[section] as any, [key]: value } : { [key]: value },
    }));
  };

  // Helper for top-level fields
  const updateTopField = (key: keyof UserData, value: any) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  // Appointment History tab state
  const [activeTab, setActiveTab] = useState<'info' | 'appointments'>('info');
  const [appointments, setAppointments] = useState<any[] | null>(null);
  const [isLoadingAppointments, setIsLoadingAppointments] = useState(false);

  const fetchAppointments = useCallback(async () => {
    if (appointments !== null) return;
    setIsLoadingAppointments(true);
    try {
      const { data, error } = await supabase
        .from('appointments')
        .select(`
          *,
          appointment_services (*, service:services (name, duration, price)),
          staff:team (name, role, photo)
        `)
        .eq('client_id', user.id)
        .order('start_time', { ascending: false });
      if (error) throw error;
      setAppointments(data || []);
    } catch (err) {
      console.error('Error fetching appointments:', err);
      setAppointments([]);
    } finally {
      setIsLoadingAppointments(false);
    }
  }, [user.id, appointments]);

  useEffect(() => {
    if (activeTab === 'appointments') {
      fetchAppointments();
    }
  }, [activeTab, fetchAppointments]);

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      completed: 'bg-green-100 text-green-800',
      scheduled: 'bg-blue-100 text-blue-800',
      cancelled: 'bg-red-100 text-red-800',
      no_show: 'bg-orange-100 text-orange-800',
      pending_payment: 'bg-yellow-100 text-yellow-800',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status?.replace(/_/g, ' ')}
      </span>
    );
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b flex justify-between items-center sticky top-0 bg-white z-10">
          <h3 className="text-lg font-medium text-gray-900">
            User Details: {form.full_name}
          </h3>
          <button
            className="text-gray-400 hover:text-gray-500"
            onClick={onClose}
          >
            ✕
          </button>
        </div>
        {/* Tab Navigation */}
        <div className="flex border-b">
          <button
            className={`px-6 py-3 text-sm font-medium transition-colors ${activeTab === 'info' ? 'bg-[#3A5E63] text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
            onClick={() => setActiveTab('info')}
          >
            Client Info
          </button>
          <button
            className={`px-6 py-3 text-sm font-medium transition-colors ${activeTab === 'appointments' ? 'bg-[#3A5E63] text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
            onClick={() => setActiveTab('appointments')}
          >
            Appointment History
          </button>
        </div>
        {activeTab === 'info' && <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-md font-semibold text-gray-700 mb-4">Basic Information</h4>
              <div className="space-y-3">
                <div>
                  <span className="text-sm text-gray-500">Email:</span>
                  <input className="font-medium w-full" type="email" value={form.email} onChange={e => updateTopField('email', e.target.value)} />
                </div>
                <div>
                  <span className="text-sm text-gray-500">Role:</span>
                  <select className="font-medium w-full capitalize" value={form.role} onChange={e => updateTopField('role', e.target.value)}>
                    <option value="admin">Admin</option>
                    <option value="crew">Crew</option>
                    <option value="customer">Customer</option>
                  </select>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Status:</span>
                  <select className="font-medium w-full" value={form.account_status} onChange={e => updateTopField('account_status', e.target.value)}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Created:</span>
                  <div className="font-medium">{form.created_at}</div>
                </div>
              </div>
            </div>
            {/* Profile Info */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-md font-semibold text-gray-700 mb-4">Profile Information</h4>
              {form.profile ? (
                <div className="space-y-3">
                  <div>
                    <span className="text-sm text-gray-500">Phone:</span>
                    <input className="font-medium w-full" type="text" value={form.profile.phone_number || ''} onChange={e => updateField('profile', 'phone_number', e.target.value)} />
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Address:</span>
                    <input className="font-medium w-full" type="text" value={form.profile.address_line1 || ''} placeholder="Address Line 1" onChange={e => updateField('profile', 'address_line1', e.target.value)} />
                    <input className="font-medium w-full mt-1" type="text" value={form.profile.address_line2 || ''} placeholder="Address Line 2" onChange={e => updateField('profile', 'address_line2', e.target.value)} />
                    <input className="font-medium w-full mt-1" type="text" value={form.profile.city || ''} placeholder="City" onChange={e => updateField('profile', 'city', e.target.value)} />
                    <input className="font-medium w-full mt-1" type="text" value={form.profile.state || ''} placeholder="State" onChange={e => updateField('profile', 'state', e.target.value)} />
                    <input className="font-medium w-full mt-1" type="text" value={form.profile.zip_code || ''} placeholder="Zip Code" onChange={e => updateField('profile', 'zip_code', e.target.value)} />
                    <input className="font-medium w-full mt-1" type="text" value={form.profile.country || ''} placeholder="Country" onChange={e => updateField('profile', 'country', e.target.value)} />
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Gender/Pronouns:</span>
                    <input className="font-medium w-full" type="text" value={form.profile.gender || ''} placeholder="Gender" onChange={e => updateField('profile', 'gender', e.target.value)} />
                    <input className="font-medium w-full mt-1" type="text" value={form.profile.pronouns || ''} placeholder="Pronouns" onChange={e => updateField('profile', 'pronouns', e.target.value)} />
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Language Preference:</span>
                    <input className="font-medium w-full" type="text" value={form.profile.language_preference || ''} onChange={e => updateField('profile', 'language_preference', e.target.value)} />
                  </div>
                </div>
              ) : (
                <div className="text-gray-500 italic">No profile information available</div>
              )}
            </div>
          </div>
          {/* Preferences Section */}
          <div className="mt-8 bg-gray-50 p-4 rounded-lg">
            <h4 className="text-md font-semibold text-gray-700 mb-4">Client Preferences</h4>
            {form.preferences ? (
              <div className="space-y-2">
                <div><span className="text-sm text-gray-500">Preferred Therapists:</span> <input className="font-medium w-full" type="text" value={form.preferences.preferred_therapists?.join(', ') || ''} onChange={e => updateField('preferences', 'preferred_therapists', e.target.value.split(',').map((s: string) => s.trim()))} /></div>
                <div><span className="text-sm text-gray-500">Favorite Services:</span> <input className="font-medium w-full" type="text" value={form.preferences.favorite_services?.join(', ') || ''} onChange={e => updateField('preferences', 'favorite_services', e.target.value.split(',').map((s: string) => s.trim()))} /></div>
                <div><span className="text-sm text-gray-500">Email Notifications:</span> <input type="checkbox" checked={form.preferences.email_notifications} onChange={e => updateField('preferences', 'email_notifications', e.target.checked)} /></div>
                <div><span className="text-sm text-gray-500">SMS Notifications:</span> <input type="checkbox" checked={form.preferences.sms_notifications} onChange={e => updateField('preferences', 'sms_notifications', e.target.checked)} /></div>
                <div><span className="text-sm text-gray-500">Push Notifications:</span> <input type="checkbox" checked={form.preferences.push_notifications} onChange={e => updateField('preferences', 'push_notifications', e.target.checked)} /></div>
                <div><span className="text-sm text-gray-500">Marketing Emails:</span> <input type="checkbox" checked={form.preferences.marketing_emails} onChange={e => updateField('preferences', 'marketing_emails', e.target.checked)} /></div>
                <div><span className="text-sm text-gray-500">Special Requests:</span> <input className="font-medium w-full" type="text" value={form.preferences.special_requests || ''} onChange={e => updateField('preferences', 'special_requests', e.target.value)} /></div>
                <div><span className="text-sm text-gray-500">Room Temp Preference:</span> <input className="font-medium w-full" type="text" value={form.preferences.room_temp_preference || ''} onChange={e => updateField('preferences', 'room_temp_preference', e.target.value)} /></div>
                <div><span className="text-sm text-gray-500">Music Preference:</span> <input className="font-medium w-full" type="text" value={form.preferences.music_preference || ''} onChange={e => updateField('preferences', 'music_preference', e.target.value)} /></div>
                <div><span className="text-sm text-gray-500">Pressure Preference:</span> <input className="font-medium w-full" type="text" value={form.preferences.pressure_preference || ''} onChange={e => updateField('preferences', 'pressure_preference', e.target.value)} /></div>
              </div>
            ) : (
              <div className="text-gray-500 italic">No data available</div>
            )}
          </div>
          {/* Health Profile Section */}
          <div className="mt-8 bg-gray-50 p-4 rounded-lg">
            <h4 className="text-md font-semibold text-gray-700 mb-4">Health Profile</h4>
            {form.health ? (
              <div className="space-y-2">
                <div><span className="text-sm text-gray-500">Medical Conditions:</span> <input className="font-medium w-full" type="text" value={form.health.medical_conditions?.join(', ') || ''} onChange={e => updateField('health', 'medical_conditions', e.target.value.split(',').map((s: string) => s.trim()))} /></div>
                <div><span className="text-sm text-gray-500">Allergies:</span> <input className="font-medium w-full" type="text" value={form.health.allergies?.join(', ') || ''} onChange={e => updateField('health', 'allergies', e.target.value.split(',').map((s: string) => s.trim()))} /></div>
                <div><span className="text-sm text-gray-500">Is Pregnant:</span> <input type="checkbox" checked={form.health.is_pregnant} onChange={e => updateField('health', 'is_pregnant', e.target.checked)} /></div>
                <div><span className="text-sm text-gray-500">Medications:</span> <input className="font-medium w-full" type="text" value={form.health.medications?.join(', ') || ''} onChange={e => updateField('health', 'medications', e.target.value.split(',').map((s: string) => s.trim()))} /></div>
                <div><span className="text-sm text-gray-500">Contraindications:</span> <input className="font-medium w-full" type="text" value={form.health.contraindications?.join(', ') || ''} onChange={e => updateField('health', 'contraindications', e.target.value.split(',').map((s: string) => s.trim()))} /></div>
                <div><span className="text-sm text-gray-500">Emergency Contact Name:</span> <input className="font-medium w-full" type="text" value={form.health.emergency_contact_name || ''} onChange={e => updateField('health', 'emergency_contact_name', e.target.value)} /></div>
                <div><span className="text-sm text-gray-500">Emergency Contact Phone:</span> <input className="font-medium w-full" type="text" value={form.health.emergency_contact_phone || ''} onChange={e => updateField('health', 'emergency_contact_phone', e.target.value)} /></div>
                <div><span className="text-sm text-gray-500">Emergency Contact Relationship:</span> <input className="font-medium w-full" type="text" value={form.health.emergency_contact_relationship || ''} onChange={e => updateField('health', 'emergency_contact_relationship', e.target.value)} /></div>
                <div><span className="text-sm text-gray-500">Waiver Signed:</span> <input type="checkbox" checked={form.health.waiver_signed} onChange={e => updateField('health', 'waiver_signed', e.target.checked)} /></div>
                <div><span className="text-sm text-gray-500">Waiver Signed Date:</span> <input className="font-medium w-full" type="text" value={form.health.waiver_signed_date || ''} onChange={e => updateField('health', 'waiver_signed_date', e.target.value)} /></div>
              </div>
            ) : (
              <div className="text-gray-500 italic">No data available</div>
            )}
          </div>
          {/* Loyalty Account Section */}
          <div className="mt-8 bg-gray-50 p-4 rounded-lg">
            <h4 className="text-md font-semibold text-gray-700 mb-4">Loyalty Account</h4>
            {form.loyalty ? (
              <div className="space-y-2">
                <div><span className="text-sm text-gray-500">Points:</span> <input className="font-medium w-full" type="number" value={form.loyalty.points} onChange={e => updateField('loyalty', 'points', Number(e.target.value))} /></div>
                <div><span className="text-sm text-gray-500">Tier:</span> <input className="font-medium w-full" type="text" value={form.loyalty.tier || ''} onChange={e => updateField('loyalty', 'tier', e.target.value)} /></div>
                <div><span className="text-sm text-gray-500">Membership Expiry:</span> <input className="font-medium w-full" type="text" value={form.loyalty.membership_expiry || ''} onChange={e => updateField('loyalty', 'membership_expiry', e.target.value)} /></div>
                <div><span className="text-sm text-gray-500">Referral Code:</span> <input className="font-medium w-full" type="text" value={form.loyalty.referral_code || ''} onChange={e => updateField('loyalty', 'referral_code', e.target.value)} /></div>
                <div><span className="text-sm text-gray-500">Referral Count:</span> <input className="font-medium w-full" type="number" value={form.loyalty.referral_count} onChange={e => updateField('loyalty', 'referral_count', Number(e.target.value))} /></div>
                <div><span className="text-sm text-gray-500">Available Rewards:</span> <input className="font-medium w-full" type="text" value={form.loyalty.available_rewards?.join(', ') || ''} onChange={e => updateField('loyalty', 'available_rewards', e.target.value.split(',').map((s: string) => s.trim()))} /></div>
              </div>
            ) : (
              <div className="text-gray-500 italic">No data available</div>
            )}
          </div>
          {/* Save Button */}
          <div className="mt-8 flex justify-end">
            <button
              className="bg-[#3A5E63] text-white px-4 py-2 rounded hover:bg-[#2a4548] transition-colors"
              onClick={() => onSave(form)}
            >
              Save
            </button>
          </div>
        </div>}
        {activeTab === 'appointments' && (
          <div className="p-6">
            {isLoadingAppointments ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3A5E63]"></div>
                <span className="ml-3 text-gray-500">Loading appointments...</span>
              </div>
            ) : !appointments || appointments.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <p className="text-lg">No appointments found</p>
                <p className="text-sm mt-1">This client has no appointment history.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {appointments.map((appt: any) => (
                  <div key={appt.id} className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-medium text-gray-900">
                        {appt.start_time ? new Date(appt.start_time + 'Z').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'America/Chicago' }) : 'No date'}
                        {appt.end_time && ` - ${new Date(appt.end_time + 'Z').toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'America/Chicago' })}`}
                      </div>
                      <div className="flex gap-2">
                      </div>
                    </div>
                    <div className="text-sm text-gray-600">
                      {appt.appointment_services?.length > 0 && (
                        <span>
                          {appt.appointment_services.map((as: any) => as.service?.name).filter(Boolean).join(', ')}
                        </span>
                      )}
                      {appt.staff?.name && (
                        <span className="ml-2">| Staff: {appt.staff.name}</span>
                      )}
                    </div>
                    {appt.crew_notes && (
                      <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded p-3">
                        <span className="text-xs font-medium text-yellow-800">Crew Notes:</span>
                        <p className="text-sm text-yellow-900 mt-1">{appt.crew_notes}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const UserManagement: React.FC<UserManagementProps> = ({ mode = 'admin' }) => {
  const navigate = useNavigate();

  // State variables
  const [users, setUsers] = useState<UserData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [membershipFilter, setMembershipFilter] = useState<'all' | 'member' | 'non-member'>('all');
  const [currentUser, setCurrentUser] = useState<UserData | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<UserData | null>(null);
  const [cancelMemberTarget, setCancelMemberTarget] = useState<UserData | null>(null);
  const [creatingFormForId, setCreatingFormForId] = useState<string | null>(null);
  
  // Toast functions
  const addToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
  };
  
  const removeToast = (id: number) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  // Fetch users data
  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Fetch user profiles
      const { data: profiles, error: profilesError } = await supabase
        .from('user_profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (profilesError) {
        throw new Error(`Error fetching profiles: ${profilesError.message}`);
      }

      // Fetch client preferences
      const { data: preferences } = await supabase
        .from('client_preferences')
        .select('*');

      // Fetch health profiles
      const { data: healthProfiles } = await supabase
        .from('health_profiles')
        .select('*');

      // Fetch loyalty accounts
      const { data: loyaltyAccounts } = await supabase
        .from('loyalty_accounts')
        .select('*');

      // Fetch consent forms
      const { data: consentForms, error: consentFormsError } = await supabase
        .from('facial_consent_forms')
        .select('id, user_id, email, phone, form_token, signature_url');

      if (consentFormsError) {
        console.error('Error fetching consent forms:', consentFormsError);
      }

      // Map the data - use only public data from user_profiles
      const userData: UserData[] = profiles?.map(profile => {
        const userPreferences = preferences?.find(p => p.user_id === profile.id) || null;
        const userHealth = healthProfiles?.find(h => h.user_id === profile.id) || null;
        const userLoyalty = loyaltyAccounts?.find(l => l.user_id === profile.id) || null;

        // Find consent form: prefer FK link, then user_id, email, phone fallback
        const digitsOnly = (s: string) => s.replace(/\D/g, '');
        let consentForm = null;
        if (profile.facial_consent_form_id) {
          consentForm = consentForms?.find(f => f.id === profile.facial_consent_form_id) || null;
        }
        if (!consentForm) {
          consentForm = consentForms?.find(f => f.user_id === profile.id) || null;
        }
        if (!consentForm && profile.email) {
          consentForm = consentForms?.find(f => f.email === profile.email) || null;
        }
        if (!consentForm && profile.phone_number) {
          const profileDigits = digitsOnly(profile.phone_number);
          if (profileDigits.length >= 10) {
            consentForm = consentForms?.find(f =>
              f.phone && digitsOnly(f.phone).endsWith(profileDigits.slice(-10))
            ) || null;
          }
        }



        return {
          id: profile.id,
          email: profile.email || 'Unknown Email',
          full_name: profile.full_name || 'Unnamed User',
          role: profile.role || 'customer',
          account_status: profile.account_status || 'active',
          created_at: profile.created_at,
          profile,
          preferences: userPreferences,
          health: userHealth,
          loyalty: userLoyalty,
          membership_status: profile.membership_status || null,
          membership_started_at: profile.membership_started_at || null,
          stripe_subscription_id: profile.stripe_subscription_id || null,
          consent_form_token: consentForm?.form_token || null,
          consent_form_signed: consentForm ? !!consentForm.signature_url : null,
        };
      }) || [];

      setUsers(userData);
    } catch (err: any) {
      setError(err.message || 'An error occurred while fetching users');
      addToast(`Failed to load users: ${err.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Filter users based on search and filters
  const filteredUsers = users.filter(user => {
    const matchesSearch =
      user.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    const matchesStatus = statusFilter === 'all' || user.account_status === statusFilter;

    const isMember = user.membership_status === 'active';
    const matchesMembership =
      membershipFilter === 'all' ||
      (membershipFilter === 'member' && isMember) ||
      (membershipFilter === 'non-member' && !isMember);

    return matchesSearch && matchesRole && matchesStatus && matchesMembership;
  });

  // Open modal to create a new user
  const handleAddUser = () => {
    setCurrentUser(null);
    setIsModalOpen(true);
  };

  // Open modal to view/edit a user
  const handleViewUser = (user: UserData) => {
    setCurrentUser(user);
    setIsViewModalOpen(true);
  };

  // Handle saving user (create or update)
  const handleSaveUser = async (userData: any) => {
    try {
      setIsLoading(true);
      
      // Create a new user
      if (!userData.id) {
        const phone = userData.phone;
        const normalizedPhone = normalizePhoneForLookup(phone);
        if (!normalizedPhone) {
          addToast('Invalid phone number. Please enter a valid 10-digit US/Canada number.', 'error');
          return;
        }

        // Check for existing user by phone or email
        const { data: existingUser } = await supabase
          .rpc('find_profile_by_phone_or_email', {
            p_phone: normalizedPhone,
            p_email: userData.email || null,
          });

        if (existingUser && existingUser.length > 0) {
          addToast('A user with this phone number or email already exists.', 'error');
          return;
        }

        const fullName = `${userData.firstName} ${userData.lastName}`;
        const email = userData.email || generateGuestPseudoEmail(normalizedPhone);

        const { data, error } = await supabase
          .rpc('get_or_create_guest_profile', {
            p_email: email,
            p_full_name: fullName,
            p_preferred_name: userData.firstName,
            p_phone: phone,
          });

        if (error) {
          throw new Error(`Error creating customer: ${error.message}`);
        }

        addToast('Customer added successfully', 'success');
      } else {
        // Update user profile
        const { error: updateError } = await supabase
          .from('user_profiles')
          .update({ 
            full_name: userData.full_name,
            preferred_name: userData.email,
            account_status: userData.account_status 
          })
          .eq('id', userData.id);
        
        if (updateError) {
          throw new Error(`Error updating user profile: ${updateError.message}`);
        }
        
        // Update user role
        const { error: roleError } = await supabase
          .from('user_roles')
          .update({ role: userData.role })
          .eq('user_id', userData.id);
          
        if (roleError) {
          console.error('Error updating role:', roleError);
        }
        
        addToast('User updated successfully', 'success');
      }
      
      // Refresh users
      await fetchUsers();
      
      setIsModalOpen(false);
    } catch (err: any) {
      console.error('Error saving user:', err);
      addToast(`Failed to save user: ${err.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle changing user status
  const handleStatusChange = async (userId: string, newStatus: string) => {
    try {
      setIsLoading(true);

      const { error } = await supabase
        .from('user_profiles')
        .update({ account_status: newStatus })
        .eq('id', userId);

      if (error) {
        throw new Error(`Error updating status: ${error.message}`);
      }

      // Update local state
      setUsers(users.map(user =>
        user.id === userId ? { ...user, account_status: newStatus } : user
      ));

      addToast(`User status updated to ${newStatus}`, 'success');
    } catch (err: any) {
      console.error('Error changing status:', err);
      addToast(`Failed to update status: ${err.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle password reset
  const handleResetPassword = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
      });

      if (error) {
        throw new Error(`Error sending reset email: ${error.message}`);
      }

      addToast(`Password reset email sent to ${email}`, 'success');
    } catch (err: any) {
      console.error('Error resetting password:', err);
      addToast(`Failed to send reset email: ${err.message}`, 'error');
    }
  };

  // Handle delete user (soft delete with anonymization via Edge Function)
  const handleDeleteUser = async () => {
    if (!deleteTarget) return;

    setDeleteTarget(null);

    try {
      setIsLoading(true);

      // Get auth session for API call
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Session expired. Please log in again.');
      }

      // Call Edge Function to properly delete account
      const response = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/delete-user-account`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userId: deleteTarget.id,
            deletionReason: 'Admin deleted account',
            isAdminDelete: true
          })
        }
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to delete user account');
      }

      addToast(
        `User account deleted successfully.`,
        'success'
      );

      // Refresh user list
      await fetchUsers();
    } catch (err: any) {
      console.error('Error deleting user:', err);
      addToast(`Failed to delete user: ${err.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle cancel membership for a user (admin bypass)
  const handleCancelMembership = async () => {
    if (!cancelMemberTarget) return;

    setCancelMemberTarget(null);

    try {
      setIsLoading(true);

      // Get auth session for API call
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Session expired. Please log in again.');
      }

      // Call Edge Function to cancel subscription with admin bypass
      const response = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/cancel-subscription`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            adminBypass: true,
            targetUserId: cancelMemberTarget.id
          })
        }
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || result.message || 'Failed to cancel membership');
      }

      addToast(
        `Membership cancelled for ${cancelMemberTarget.full_name}. Access ends: ${new Date(result.cancel_at).toLocaleDateString()}`,
        'success'
      );

      // Refresh user list
      await fetchUsers();
    } catch (err: any) {
      console.error('Error cancelling membership:', err);
      addToast(`Failed to cancel membership: ${err.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle creating consent form draft for a user
  const handleCreateConsentForm = async (user: UserData) => {
    try {
      setCreatingFormForId(user.id);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Session expired. Please log in again.');
      }

      const response = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/create-consent-form-draft`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ targetUserId: user.id }),
        }
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to create consent form');
      }

      addToast(`Consent form created for ${user.full_name}`, 'success');
      await fetchUsers();
    } catch (err: any) {
      console.error('Error creating consent form:', err);
      addToast(`Failed to create consent form: ${err.message}`, 'error');
    } finally {
      setCreatingFormForId(null);
    }
  };

  // Formatting date for display
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';

    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Add this function to toggle dropdowns
  const toggleDropdown = (userId: string) => {
    if (openDropdownId === userId) {
      setOpenDropdownId(null);
    } else {
      setOpenDropdownId(userId);
    }
  };
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setOpenDropdownId(null);
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  // Loading state
  if (isLoading && users.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-[#B2D8C5] border-t-[#232347] rounded-full animate-spin mb-4"></div>
          <p className="text-[#232347]">Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pt-2">
      {/* CSS Styles */}
      <style>{dropdownStyles}</style>
      
      {/* Toast container */}
      <div className="fixed bottom-4 right-4 z-50 space-y-2">
        {toasts.map(toast => (
          <Toast 
            key={toast.id} 
            toast={toast} 
            onClose={() => removeToast(toast.id)} 
          />
        ))}
      </div>
      
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
        <h1 className="text-2xl font-semibold text-[#344854]">User Management</h1>
        <div className="flex items-center gap-3">
          <MessageNotificationBell />
          <NotificationBell />
          <button
            className="bg-[#3A5E63] text-white px-4 py-2 rounded hover:bg-[#2a4548] transition-colors inline-flex items-center"
            onClick={handleAddUser}
          >
            <span className="mr-2">+</span> Add New User
          </button>
        </div>
      </div>
      
      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {error}
        </div>
      )}
      
      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
              Search
            </label>
            <input
              type="text"
              id="search"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Search by name or email"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>

          <div>
            <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
              Role
            </label>
            <select
              id="role"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              value={roleFilter}
              onChange={e => setRoleFilter(e.target.value)}
            >
              <option value="all">All Roles</option>
              <option value="admin">Admin</option>
              <option value="crew">Crew</option>
              <option value="customer">Customer</option>
            </select>
          </div>

          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              id="status"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="guest">Guest</option>
              <option value="pending_verification">Pending Verification</option>
              <option value="inactive">Inactive</option>
              <option value="suspended">Suspended</option>
              <option value="deleted">Deleted</option>
            </select>
          </div>

          <div>
            <label htmlFor="membership" className="block text-sm font-medium text-gray-700 mb-1">
              Membership
            </label>
            <select
              id="membership"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              value={membershipFilter}
              onChange={e => setMembershipFilter(e.target.value as 'all' | 'member' | 'non-member')}
            >
              <option value="all">All Users</option>
              <option value="member">Members Only</option>
              <option value="non-member">Non-Members</option>
            </select>
          </div>
        </div>
      </div>
      
      {/* Users Table */}
      <div className="bg-white rounded-lg shadow overflow-visible">
        <div className="overflow-x-auto">
          <table className="min-w-full table-fixed divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th scope="col" className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Number
                </th>
                <th scope="col" className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th scope="col" className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Membership
                </th>
                <th scope="col" className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Balance
                </th>
                <th scope="col" className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Form
                </th>
                <th scope="col" className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.map(user => (
                <tr key={user.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/booking?clientId=${user.id}`)}>
                  <td className="px-3 py-4 whitespace-nowrap">
                    <div className="font-medium text-gray-900">{user.full_name}</div>
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap">
                    <div className="text-gray-500 max-w-[180px] truncate" title={user.email}>{user.email}</div>
                  </td>
                  <td className="px-2 py-4 whitespace-nowrap">
                    <div className="text-gray-500 max-w-[120px] truncate" title={user.profile?.phone_number || ''}>{user.profile?.phone_number || '-'}</div>
                  </td>
                  <td className="px-2 py-4 whitespace-nowrap">
                    <div className="capitalize">{user.role}</div>
                  </td>
                  <td className="px-2 py-4 whitespace-nowrap">
                    <StatusBadge status={user.account_status} />
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap">
                    {user.membership_status === 'active' ? (
                      <div>
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          Member
                        </span>
                        {user.membership_started_at && (
                          <div className="text-xs text-gray-500 mt-1">
                            Since: {new Date(user.membership_started_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </div>
                        )}
                      </div>
                    ) : user.membership_status === 'cancelled' ? (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                        Cancelled
                      </span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-2 py-4 whitespace-nowrap text-gray-500">
                    {user.role === 'customer'
                      ? `$${(user.profile?.wallet_balance || 0).toFixed(2)}`
                      : 'N/A'}
                  </td>
                  <td className="px-2 py-4 whitespace-nowrap">
                    {user.role === 'customer' ? (
                      user.consent_form_signed !== null ? (
                        <span onClick={(e) => e.stopPropagation()}>
                          <FormStatusBadge
                            isComplete={user.consent_form_signed}
                            size="sm"
                            iconOnly={true}
                            onClick={user.consent_form_token
                              ? () => window.open(`/consent-form?token=${user.consent_form_token}`, '_blank')
                              : undefined}
                          />
                        </span>
                      ) : (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleCreateConsentForm(user); }}
                          disabled={creatingFormForId === user.id}
                          className="text-blue-600 hover:text-blue-800 text-xs disabled:text-gray-400 disabled:cursor-not-allowed"
                        >
                          {creatingFormForId === user.id ? 'Creating...' : '+ Create'}
                        </button>
                      )
                    ) : (
                      <span className="text-gray-300 text-sm">-</span>
                    )}
                  </td>
                  <td className="px-2 py-4 whitespace-nowrap text-gray-500">
                    {user.created_at ? new Date(user.created_at).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' }) : 'Never'}
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleViewUser(user); }}
                      className="text-indigo-600 hover:text-indigo-900 mr-3"
                    >
                      View
                    </button>
                    {user.account_status === 'deleted' ? (
                      <span className="text-gray-400 text-sm italic">No actions available</span>
                    ) : (
                      <div className="relative inline-block text-left">
                        <button
                          type="button"
                          className="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-3 py-1 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleDropdown(user.id);
                          }}
                        >
                          Actions ▼
                        </button>
                        <div
                          className={`origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50 ${openDropdownId === user.id ? 'block' : 'hidden'}`}
                        >
                          <div className="py-1 flex flex-col" role="menu" aria-orientation="vertical">
                            {/* Activate/Deactivate - only for non-guest users */}
                            {user.account_status !== 'guest' && (
                              <button
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 whitespace-nowrap"
                                role="menuitem"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const newStatus = user.account_status === 'active' ? 'inactive' : 'active';
                                  handleStatusChange(user.id, newStatus);
                                  setOpenDropdownId(null);
                                }}
                              >
                                {user.account_status === 'active' ? 'Deactivate' : 'Activate'}
                              </button>
                            )}
                            {/* Reset Password - only for users with auth accounts (non-guest) */}
                            {user.account_status !== 'guest' && (
                              <button
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 whitespace-nowrap"
                                role="menuitem"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  setOpenDropdownId(null);
                                  await handleResetPassword(user.email);
                                }}
                              >
                                Reset Password
                              </button>
                            )}
                            {/* Guest-specific info */}
                            {user.account_status === 'guest' && (
                              <div className="px-4 py-2 text-xs text-gray-500 border-b">
                                Guest user - no login account
                              </div>
                            )}
                            {/* Cancel Membership - only for active members */}
                            {user.membership_status === 'active' && (
                              <button
                                className="w-full text-left px-4 py-2 text-sm text-orange-600 hover:bg-gray-100 hover:text-orange-900 whitespace-nowrap"
                                role="menuitem"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenDropdownId(null);
                                  setCancelMemberTarget(user);
                                }}
                              >
                                Cancel Membership
                              </button>
                            )}
                            {/* Delete - available for all non-deleted users, hidden for crew mode */}
                            {mode === 'admin' && (
                              <button
                                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 hover:text-red-900 whitespace-nowrap"
                                role="menuitem"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenDropdownId(null);
                                  setDeleteTarget(user);
                                }}
                              >
                                Delete Account
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredUsers.length === 0 && (
          <div className="py-8 text-center text-gray-500">
            No users found matching your filters.
          </div>
        )}
        
        {/* Pagination - simplified for now */}
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">{filteredUsers.length}</span> users
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Add/Edit User Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">
                {currentUser ? 'Edit User' : 'Add New Customer'}
              </h3>
              <button
                className="text-gray-400 hover:text-gray-500"
                onClick={() => setIsModalOpen(false)}
              >
                ✕
              </button>
            </div>
            
            <div className="p-6">
              <form className="space-y-4">
                {!currentUser ? (
                  <>
                    <div>
                      <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 mb-1">
                        First Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="first_name"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="First name"
                      />
                    </div>
                    <div>
                      <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-1">
                        Last Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="last_name"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="Last name"
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                        Phone Number <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="(555) 123-4567"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        Email <span className="text-gray-400 text-xs font-normal">(optional)</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="email@example.com"
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <label htmlFor="full_name" className="block text-sm font-medium text-gray-700 mb-1">
                        Full Name
                      </label>
                      <input
                        type="text"
                        id="full_name"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        defaultValue={currentUser?.full_name || ''}
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        Email
                      </label>
                      <input
                        type="text"
                        id="email"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        defaultValue={currentUser?.email || ''}
                      />
                    </div>
                    <div>
                      <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
                        Role
                      </label>
                      <select
                        id="role"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        defaultValue={currentUser?.role || 'customer'}
                      >
                        <option value="admin">Admin</option>
                        <option value="crew">Crew</option>
                        <option value="customer">Customer</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="account_status" className="block text-sm font-medium text-gray-700 mb-1">
                        Status
                      </label>
                      <select
                        id="account_status"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        defaultValue={currentUser.account_status || 'active'}
                      >
                        <option value="active">Active</option>
                        <option value="guest">Guest</option>
                        <option value="pending_verification">Pending Verification</option>
                        <option value="inactive">Inactive</option>
                        <option value="suspended">Suspended</option>
                        <option value="deleted">Deleted</option>
                      </select>
                    </div>
                  </>
                )}
              </form>
            </div>

            <div className="px-6 py-4 border-t bg-gray-50 flex justify-end">
              <button
                type="button"
                className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 mr-3"
                onClick={() => setIsModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-[#3A5E63] hover:bg-[#2a4548] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#3A5E63]"
                onClick={() => {
                  if (!currentUser) {
                    // Create new customer flow
                    const firstNameEl = document.getElementById('first_name') as HTMLInputElement;
                    const lastNameEl = document.getElementById('last_name') as HTMLInputElement;
                    const phoneEl = document.getElementById('phone') as HTMLInputElement;
                    const emailEl = document.getElementById('email') as HTMLInputElement;

                    if (!firstNameEl.value.trim()) {
                      addToast('Please enter a first name', 'error');
                      firstNameEl.focus();
                      return;
                    }
                    if (!lastNameEl.value.trim()) {
                      addToast('Please enter a last name', 'error');
                      lastNameEl.focus();
                      return;
                    }
                    if (!phoneEl.value.trim()) {
                      addToast('Please enter a phone number', 'error');
                      phoneEl.focus();
                      return;
                    }
                    const phoneDigits = phoneEl.value.replace(/\D/g, '');
                    if (phoneDigits.length < 10) {
                      addToast('Phone number must be at least 10 digits', 'error');
                      phoneEl.focus();
                      return;
                    }

                    handleSaveUser({
                      firstName: firstNameEl.value.trim(),
                      lastName: lastNameEl.value.trim(),
                      phone: phoneEl.value.trim(),
                      email: emailEl.value.trim() || '',
                    });
                  } else {
                    // Edit existing user flow
                    const fullNameEl = document.getElementById('full_name') as HTMLInputElement;
                    const emailEl = document.getElementById('email') as HTMLInputElement;
                    const roleEl = document.getElementById('role') as HTMLSelectElement;
                    const statusEl = document.getElementById('account_status') as HTMLSelectElement;

                    if (!fullNameEl.value.trim()) {
                      addToast('Please enter a name', 'error');
                      fullNameEl.focus();
                      return;
                    }

                    handleSaveUser({
                      id: currentUser?.id,
                      full_name: fullNameEl.value.trim(),
                      email: emailEl.value.trim(),
                      role: roleEl.value,
                      account_status: statusEl ? statusEl.value : 'active',
                    });
                  }
                }}
              >
                {currentUser ? 'Update User' : 'Add Customer'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* View User Details Modal */}
      {isViewModalOpen && currentUser && (
        <UserDetailsModal
          user={currentUser}
          onClose={() => setIsViewModalOpen(false)}
          onSave={async (updatedUser) => {
            setIsLoading(true);
            try {
              // Update user_profiles
              await supabase.from('user_profiles').update({
                email: updatedUser.email,
                full_name: updatedUser.full_name,
                phone_number: updatedUser.profile?.phone_number,
                address_line1: updatedUser.profile?.address_line1,
                address_line2: updatedUser.profile?.address_line2,
                city: updatedUser.profile?.city,
                state: updatedUser.profile?.state,
                zip_code: updatedUser.profile?.zip_code,
                country: updatedUser.profile?.country,
                gender: updatedUser.profile?.gender,
                pronouns: updatedUser.profile?.pronouns,
                language_preference: updatedUser.profile?.language_preference,
                account_status: updatedUser.account_status,
              }).eq('id', updatedUser.id);

              // Update client_preferences
              if (updatedUser.preferences) {
                await supabase.from('client_preferences').update({
                  preferred_therapists: updatedUser.preferences.preferred_therapists,
                  favorite_services: updatedUser.preferences.favorite_services,
                  email_notifications: updatedUser.preferences.email_notifications,
                  sms_notifications: updatedUser.preferences.sms_notifications,
                  push_notifications: updatedUser.preferences.push_notifications,
                  marketing_emails: updatedUser.preferences.marketing_emails,
                  special_requests: updatedUser.preferences.special_requests,
                  room_temp_preference: updatedUser.preferences.room_temp_preference,
                  music_preference: updatedUser.preferences.music_preference,
                  pressure_preference: updatedUser.preferences.pressure_preference,
                }).eq('user_id', updatedUser.id);
              }

              // Update health_profiles
              if (updatedUser.health) {
                await supabase.from('health_profiles').update({
                  medical_conditions: updatedUser.health.medical_conditions,
                  allergies: updatedUser.health.allergies,
                  is_pregnant: updatedUser.health.is_pregnant,
                  medications: updatedUser.health.medications,
                  contraindications: updatedUser.health.contraindications,
                  emergency_contact_name: updatedUser.health.emergency_contact_name,
                  emergency_contact_phone: updatedUser.health.emergency_contact_phone,
                  emergency_contact_relationship: updatedUser.health.emergency_contact_relationship,
                  waiver_signed: updatedUser.health.waiver_signed,
                  waiver_signed_date: updatedUser.health.waiver_signed_date,
                }).eq('user_id', updatedUser.id);
              }

              // Update loyalty_accounts
              if (updatedUser.loyalty) {
                await supabase.from('loyalty_accounts').update({
                  points: updatedUser.loyalty.points,
                  tier: updatedUser.loyalty.tier,
                  membership_expiry: updatedUser.loyalty.membership_expiry,
                  referral_code: updatedUser.loyalty.referral_code,
                  referral_count: updatedUser.loyalty.referral_count,
                  available_rewards: updatedUser.loyalty.available_rewards,
                }).eq('user_id', updatedUser.id);
              }

              addToast('User updated successfully', 'success');
              await fetchUsers();
              setIsViewModalOpen(false);
            } catch (err) {
              addToast('Failed to update user', 'error');
            } finally {
              setIsLoading(false);
            }
          }}
        />
      )}

      {/* Delete User Confirmation Dialog */}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Delete User Account?"
        message={[
          `Delete account for:`,
          `${deleteTarget?.full_name || 'Unknown'} (${deleteTarget?.email})`,
          '',
          'This will permanently delete their',
          'profile and personal information.',
          '',
          'This action cannot be undone.'
        ]}
        confirmText="Delete Account"
        cancelText="Cancel"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
        onConfirm={handleDeleteUser}
        onCancel={() => setDeleteTarget(null)}
      />

      {/* Cancel Membership Confirmation Dialog */}
      <ConfirmDialog
        isOpen={!!cancelMemberTarget}
        title="Cancel Membership?"
        message={[
          `Cancel membership for:`,
          `${cancelMemberTarget?.full_name || 'Unknown'} (${cancelMemberTarget?.email})`,
          '',
          'This will cancel their subscription at the',
          'end of the current billing period.',
          '',
          'Note: This bypasses the 60-day commitment period.'
        ]}
        confirmText="Cancel Membership"
        cancelText="Go Back"
        confirmButtonClass="bg-orange-600 hover:bg-orange-700"
        onConfirm={handleCancelMembership}
        onCancel={() => setCancelMemberTarget(null)}
      />
    </div>
  );
};

export default UserManagement;
